import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { Shell } from "../components/Shell.tsx";
import {
  Button,
  Confirm,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Skeleton,
  StatusBadge,
  Switch,
} from "../components/ui.tsx";
import { t, tn, type MessageKey } from "../i18n/index.ts";
import { action } from "../lib/api.ts";
import { day } from "../lib/format.ts";
import {
  cellText,
  csvValue,
  outLabel,
  type OutCol,
  type Period,
  type Result,
  type SavedReport,
  type Spec,
} from "../lib/reports.ts";
import { useSession } from "../lib/session.tsx";
import { useUi } from "../lib/ui-state.tsx";
import { useAction, useSave } from "./manage/common.tsx";
import { ChartView, Freshness, PeriodPicker } from "./MyReports.tsx";
import { PivotTable } from "../components/PivotTable.tsx";
import { downloadCsv } from "./Reports.tsx";

/**
 * Dashboards: pages of tiles made from saved reports, each shown as a big number (with the
 * change against the period before), bars, a line, a pie, a table or a pivot. A dashboard can
 * show all its tiles for another period at once; each tile downloads as a spreadsheet.
 */

type View = "number" | "bar" | "line" | "pie" | "table" | "pivot";
type Size = "sm" | "md" | "lg";
const VIEWS: View[] = ["number", "bar", "line", "pie", "table", "pivot"];
const SIZES: Size[] = ["sm", "md", "lg"];
const VIEW_ICON: Record<
  View,
  "hash" | "chart" | "trendUp" | "circle" | "list" | "layout"
> = {
  number: "hash",
  bar: "chart",
  line: "trendUp",
  pie: "circle",
  table: "list",
  pivot: "layout",
};
const PIE_TONES = [
  "blue",
  "green",
  "orange",
  "purple",
  "teal",
  "red",
  "yellow",
  "grey",
];

interface TileSpec {
  id: string;
  report: string;
  view: View;
  size: Size;
  title?: string | null;
  compare: boolean;
}
type Problem = "needs_summary" | "needs_two" | "needs_one" | "needs_total";
interface ShownTile extends TileSpec {
  report_name: string | null;
  state: "ok" | "hidden" | "gone" | "error";
  problem?: Problem;
  error?: string;
  spec?: { group: string[]; period: Period };
  result?: Result & { total: number };
  change?: { total: number; previous: number; from: string; to: string } | null;
}
interface Board {
  id: string;
  name: string;
  shared: boolean;
  mine: boolean;
  can_edit: boolean;
  tiles: ShownTile[];
  /** The oldest figures on the page: tiles are worked out ahead and kept a few minutes. */
  computed_at?: string | null;
}
interface BoardRow {
  id: string;
  name: string;
  shared: boolean;
  tiles: number;
  mine: boolean;
  can_edit: boolean;
}

/** The same check as the server, to offer only the views a report can take. */
export function viewProblem(
  view: View,
  spec: Pick<Spec, "group" | "measures">,
): Problem | null {
  if (view === "table") return null;
  if (!spec.group.length) return "needs_summary";
  if (view === "pivot" && spec.group.length !== 2) return "needs_two";
  if (view === "pie" && spec.group.length !== 1) return "needs_one";
  if (
    view === "number" &&
    !["sum", "count"].includes(spec.measures[0]?.agg ?? "")
  )
    return "needs_total";
  return null;
}
const newId = () => Math.random().toString(36).slice(2, 10).padEnd(8, "0");

// ---- the list ----------------------------------------------------------------------------

export function Dashboards() {
  const navigate = useNavigate();
  const { data, reload } = useAction<BoardRow[]>("studio.dashboards");
  const [creating, setCreating] = useState(false);
  return (
    <Shell>
      <div className="page dashboards">
        <div className="page-head">
          <div>
            <h1>{t("dash.title")}</h1>
            <p className="muted">{t("dash.subtitle")}</p>
          </div>
          <div className="page-actions">
            <Button
              variant="primary"
              icon="plus"
              onClick={() => setCreating(true)}
            >
              {t("dash.new")}
            </Button>
          </div>
        </div>
        {!data ? (
          <Skeleton h={200} r="var(--radius-lg)" />
        ) : !data.length ? (
          <EmptyState
            icon="layout"
            title={t("dash.empty")}
            body={t("dash.empty_body")}
            action={
              <Button
                variant="primary"
                icon="plus"
                onClick={() => setCreating(true)}
              >
                {t("dash.new")}
              </Button>
            }
          />
        ) : (
          <div className="rpt-cards">
            {data.map((d) => (
              <button
                key={d.id}
                type="button"
                className="rpt-card"
                onClick={() => navigate(`/dashboards/${d.id}`)}
              >
                <span className="list-icon" aria-hidden="true">
                  <Icon name="layout" size={18} />
                </span>
                <span className="rpt-card-main">
                  <span className="list-title">{d.name}</span>
                  <span className="list-sub">{tn("dash.tiles", d.tiles)}</span>
                </span>
                {d.shared ? (
                  <StatusBadge
                    tone="info"
                    icon="users"
                    label={t("rpt.shared")}
                  />
                ) : null}
                <Icon name="chevronRight" size={18} />
              </button>
            ))}
          </div>
        )}
        {creating ? (
          <NameDialog
            onClose={() => setCreating(false)}
            onSave={async (name, shared) => {
              const d = await action<{ id: string }>("studio.save_dashboard", {
                name,
                shared,
                tiles: [],
              });
              setCreating(false);
              await reload();
              navigate(`/dashboards/${d.id}?edit=1`);
            }}
          />
        ) : null}
      </div>
    </Shell>
  );
}

function NameDialog({
  name: start = "",
  shared: startShared = false,
  onClose,
  onSave,
}: {
  name?: string;
  shared?: boolean;
  onClose: () => void;
  onSave: (name: string, shared: boolean) => Promise<void>;
}) {
  const { can } = useSession();
  const { run, busy } = useSave();
  const [name, setName] = useState(start);
  const [shared, setShared] = useState(startShared);
  return (
    <Dialog
      title={start ? t("dash.rename") : t("dash.new")}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!name.trim()}
            onClick={() => void run(() => onSave(name.trim(), shared))}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field label={t("dash.name")}>
        <input
          className="input"
          value={name}
          maxLength={80}
          placeholder={t("dash.name_ph")}
          onChange={(e) => setName(e.target.value)}
          data-autofocus
        />
      </Field>
      {can("studio.manage") ? (
        <label className="switch-row">
          <span>{t("dash.share")}</span>
          <Switch
            checked={shared}
            onChange={setShared}
            label={t("dash.share")}
          />
        </label>
      ) : null}
    </Dialog>
  );
}

// ---- one dashboard -------------------------------------------------------------------------

export function DashboardPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const [period, setPeriod] = useState<Period | null>(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [board, setBoard] = useState<Board | null>(null);
  const [editing, setEditing] = useState(() =>
    new URLSearchParams(location.search).has("edit"),
  );
  const [draft, setDraft] = useState<TileSpec[]>([]);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [fresh, setFresh] = useState(0);

  useEffect(() => {
    if (period === "custom" && (!range.from || !range.to)) return;
    let live = true;
    action<Board>("studio.dashboard", {
      id,
      ...(period ? { period, ...(period === "custom" ? range : {}) } : {}),
      fresh: fresh > 0,
    }).then(
      (b) => {
        if (!live) return;
        setBoard(b);
        setDraft(
          b.tiles.map(({ id, report, view, size, title, compare }) => ({
            id,
            report,
            view,
            size,
            title,
            compare,
          })),
        );
      },
      (err) => {
        fail(err);
        navigate("/dashboards");
      },
    );
    return () => {
      live = false;
    };
  }, [id, period, range, tick, fresh, fail, navigate]);

  const saveTiles = async (
    tiles: TileSpec[],
    extra: { name?: string; shared?: boolean } = {},
  ) => {
    if (!board) return;
    setBusy(true);
    try {
      await action("studio.save_dashboard", {
        id: board.id,
        name: extra.name ?? board.name,
        shared: extra.shared ?? board.shared,
        tiles: tiles.map((x) => ({ ...x, title: x.title || null })),
      });
      setTick((n) => n + 1);
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const move = (i: number, by: number) =>
    setDraft((d) => {
      const next = [...d];
      const [x] = next.splice(i, 1);
      next.splice(Math.max(0, Math.min(next.length, i + by)), 0, x!);
      return next;
    });
  const shown = (tile: TileSpec) => board?.tiles.find((x) => x.id === tile.id);

  return (
    <Shell>
      <div className="page dashboards">
        <nav className="prj-crumbs small" aria-label={t("dash.title")}>
          <Link to="/dashboards">{t("dash.title")}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{board?.name ?? "…"}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1>{board?.name ?? <Skeleton w={220} h={32} />}</h1>
            <p className="muted">
              {board?.shared ? t("dash.shared_hint") : t("dash.private_hint")}
            </p>
          </div>
          {board?.can_edit ? (
            <div className="page-actions">
              {editing ? (
                <>
                  <Button
                    variant="ghost"
                    icon="trash"
                    onClick={() => setDeleting(true)}
                  >
                    {t("common.delete")}
                  </Button>
                  <Button icon="edit" onClick={() => setRenaming(true)}>
                    {t("dash.rename")}
                  </Button>
                  <Button icon="plus" onClick={() => setAdding(true)}>
                    {t("dash.add_tile")}
                  </Button>
                  <Button
                    variant="primary"
                    icon="check"
                    busy={busy}
                    onClick={async () => {
                      if (await saveTiles(draft)) {
                        setEditing(false);
                        toast(t("dash.saved"));
                      }
                    }}
                  >
                    {t("dash.done")}
                  </Button>
                </>
              ) : (
                <Button icon="sliders" onClick={() => setEditing(true)}>
                  {t("dash.edit")}
                </Button>
              )}
            </div>
          ) : null}
        </div>
        <div className="dash-bar">
          <PeriodPicker
            period={period ?? "this_month"}
            range={range}
            onPeriod={(p) => setPeriod(p)}
            onRange={setRange}
          />
          {period ? (
            <Button
              variant="ghost"
              size="sm"
              icon="undo"
              onClick={() => setPeriod(null)}
            >
              {t("dash.each_own")}
            </Button>
          ) : (
            <span className="muted small">{t("dash.each_own_hint")}</span>
          )}
          {board?.computed_at ? (
            <Freshness at={board.computed_at} onRefresh={() => setFresh((n) => n + 1)} />
          ) : null}
        </div>
        {!board ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : !(editing ? draft : board.tiles).length ? (
          <EmptyState
            icon="layout"
            title={t("dash.no_tiles")}
            body={t("dash.no_tiles_body")}
            action={
              board.can_edit ? (
                <Button
                  variant="primary"
                  icon="plus"
                  onClick={() => {
                    setEditing(true);
                    setAdding(true);
                  }}
                >
                  {t("dash.add_tile")}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className={`dash-grid${editing ? " is-editing" : ""}`}>
            {(editing ? draft : board.tiles).map((tile, i) => (
              <TileCard
                key={tile.id}
                tile={tile}
                shown={shown(tile)}
                editing={editing}
                first={i === 0}
                last={i === draft.length - 1}
                onMove={(by) => move(i, by)}
                onChange={(p) =>
                  setDraft((d) =>
                    d.map((x) => (x.id === tile.id ? { ...x, ...p } : x)),
                  )
                }
                onRemove={() =>
                  setDraft((d) => d.filter((x) => x.id !== tile.id))
                }
              />
            ))}
          </div>
        )}
        {adding ? (
          <AddTileDialog
            onClose={() => setAdding(false)}
            onAdd={async (tile) => {
              const next = [...draft, tile];
              // Saved at once, so the new tile shows its figures right away.
              if (await saveTiles(next)) {
                setDraft(next);
                setAdding(false);
              }
            }}
          />
        ) : null}
        {renaming && board ? (
          <NameDialog
            name={board.name}
            shared={board.shared}
            onClose={() => setRenaming(false)}
            onSave={async (name, shared) => {
              if (await saveTiles(draft, { name, shared })) setRenaming(false);
            }}
          />
        ) : null}
        {deleting && board ? (
          <Confirm
            title={t("dash.delete_title")}
            body={t("dash.delete_body", { name: board.name })}
            confirm={t("common.delete")}
            danger
            busy={busy}
            onClose={() => setDeleting(false)}
            onConfirm={async () => {
              setBusy(true);
              try {
                await action("studio.delete_dashboard", { id: board.id });
                toast(t("dash.deleted"));
                navigate("/dashboards");
              } catch (err) {
                fail(err);
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : null}
      </div>
    </Shell>
  );
}

// ---- a tile --------------------------------------------------------------------------------

function TileCard({
  tile,
  shown,
  editing,
  first,
  last,
  onMove,
  onChange,
  onRemove,
}: {
  tile: TileSpec;
  shown: ShownTile | undefined;
  editing: boolean;
  first: boolean;
  last: boolean;
  onMove: (by: number) => void;
  onChange: (p: Partial<TileSpec>) => void;
  onRemove: () => void;
}) {
  const title =
    tile.title || shown?.title || shown?.report_name || t("dash.tile");
  const download = () => {
    const r = shown?.result;
    if (!r) return;
    downloadCsv(
      `${title}.csv`,
      r.columns.map(outLabel),
      r.rows.map((row) => row.map((v, i) => csvValue(r.columns[i]!, v))),
    );
  };
  return (
    <section
      className={`card dash-tile size-${tile.size} view-${tile.view}`}
      aria-label={title}
    >
      <header className="dash-tile-head">
        <h2 title={title}>{title}</h2>
        {editing ? null : shown?.state === "ok" ? (
          <span className="dash-tile-tools">
            <IconButton
              icon="download"
              label={t("dash.download", { name: title })}
              onClick={download}
            />
            <Link
              className="icon-btn btn-ghost"
              title={t("dash.open_report", { name: title })}
              to={`/my-reports/${tile.report}`}
              aria-label={t("dash.open_report", { name: title })}
            >
              <Icon name="external" size={18} />
            </Link>
          </span>
        ) : null}
      </header>
      {editing ? (
        <div className="dash-tile-edit">
          <Field label={t("dash.tile_title")}>
            <input
              className="input"
              value={tile.title ?? ""}
              maxLength={60}
              placeholder={shown?.report_name ?? ""}
              onChange={(e) => onChange({ title: e.target.value })}
            />
          </Field>
          <div
            className="segmented dash-sizes"
            role="radiogroup"
            aria-label={t("dash.size")}
          >
            {SIZES.map((s) => (
              <button
                key={s}
                role="radio"
                aria-checked={tile.size === s}
                className={tile.size === s ? "active" : ""}
                onClick={() => onChange({ size: s })}
              >
                {t(`dash.size_${s}` as MessageKey)}
              </button>
            ))}
          </div>
          {tile.view === "number" ? (
            <label className="switch-row">
              <span>{t("dash.compare")}</span>
              <Switch
                checked={tile.compare}
                onChange={(v) => onChange({ compare: v })}
                label={t("dash.compare")}
              />
            </label>
          ) : null}
          <div className="dash-tile-moves">
            <IconButton
              icon="back"
              label={t("dash.move_left")}
              disabled={first}
              onClick={() => onMove(-1)}
            />
            <IconButton
              icon="chevronRight"
              label={t("dash.move_right")}
              disabled={last}
              onClick={() => onMove(1)}
            />
            <span className="spacer" />
            <Button variant="ghost" size="sm" icon="trash" onClick={onRemove}>
              {t("dash.remove")}
            </Button>
          </div>
        </div>
      ) : !shown ? (
        <Skeleton h={120} />
      ) : shown.state === "hidden" ? (
        <TileNote icon="lock" text={t("dash.hidden")} />
      ) : shown.state === "gone" ? (
        <TileNote icon="alert" text={t("dash.gone")} />
      ) : shown.state === "error" ? (
        <TileNote
          icon="alert"
          text={
            shown.problem
              ? t(`dash.p_${shown.problem}` as MessageKey)
              : (shown.error ?? "")
          }
        />
      ) : (
        <TileBody tile={shown} />
      )}
    </section>
  );
}

function TileNote({ icon, text }: { icon: "lock" | "alert"; text: string }) {
  return (
    <p className="dash-note muted">
      <Icon name={icon} size={18} /> {text}
    </p>
  );
}

function TileBody({ tile }: { tile: ShownTile }) {
  const r = tile.result!;
  const range = r.from ? `${day(r.from)} – ${day(r.to!)}` : t("rpt.all_time");
  if (!r.rows.length && tile.view !== "number")
    return <TileNote icon="alert" text={t("rpt.nothing")} />;
  switch (tile.view) {
    case "number":
      return <NumberTile tile={tile} range={range} />;
    case "bar":
    case "line":
      return <ChartView chart={tile.view} result={r} />;
    case "pie":
      return <Pie result={r} />;
    case "pivot":
      return <PivotTable result={r} compact />;
    default:
      return <SmallTable result={r} report={tile.report} />;
  }
}

function NumberTile({ tile, range }: { tile: ShownTile; range: string }) {
  const r = tile.result!;
  const m = r.columns.find((c) => c.agg) as OutCol;
  const c = tile.change;
  const pct =
    c && c.previous
      ? Math.round(((c.total - c.previous) / Math.abs(c.previous)) * 100)
      : null;
  const up = c ? c.total >= c.previous : true;
  return (
    <div className="dash-number">
      <strong className="dash-number-value tabular">
        {cellText(m, r.total)}
      </strong>
      <span className="muted small">{range}</span>
      {c ? (
        <span
          className={`dash-change ${c.total === c.previous || !c.previous ? "flat" : up ? "up" : "down"}`}
        >
          <Icon name={up ? "trendUp" : "trendDown"} size={16} />
          {pct === null
            ? t("dash.was_none")
            : t("dash.change", {
                pct: `${pct > 0 ? "+" : ""}${pct}`,
                before: cellText(m, c.previous),
              })}
        </span>
      ) : null}
    </div>
  );
}

function Pie({ result }: { result: Result }) {
  const g = result.columns[0]!;
  const at = result.columns.findIndex((c) => c.agg);
  const m = result.columns[at]!;
  const all = result.rows
    .map((row) => ({
      label: cellText(g, row[0]),
      value: Math.max(0, Number(row[at]) || 0),
    }))
    .sort((a, b) => b.value - a.value);
  const top = all.slice(0, 6);
  const rest = all.slice(6).reduce((a, x) => a + x.value, 0);
  if (rest) top.push({ label: t("dash.other"), value: rest });
  const sum = top.reduce((a, x) => a + x.value, 0) || 1;
  let acc = 0;
  const R = 15.915; // circumference 100
  return (
    <div className="dash-pie">
      <svg viewBox="0 0 42 42" aria-hidden="true">
        <circle cx="21" cy="21" r={R} className="dash-pie-track" />
        {top.map((x, i) => {
          const share = (x.value / sum) * 100;
          const el = (
            <circle
              key={i}
              cx="21"
              cy="21"
              r={R}
              className={`dash-pie-seg pie-${PIE_TONES[i % PIE_TONES.length]}`}
              strokeDasharray={`${share} ${100 - share}`}
              strokeDashoffset={25 - acc}
            />
          );
          acc += share;
          return el;
        })}
      </svg>
      <ul className="dash-legend">
        {top.map((x, i) => (
          <li key={i}>
            <span
              className={`pos-dot pie-${PIE_TONES[i % PIE_TONES.length]}`}
              aria-hidden="true"
            />
            <span className="dash-legend-label">{x.label}</span>
            <span className="tabular">{cellText(m, x.value)}</span>
            <span className="muted small tabular">
              {Math.round((x.value / sum) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One group down, the other across, with totals: the report as a spreadsheet would show it. */
function SmallTable({ result, report }: { result: Result; report: string }) {
  const rows = result.rows.slice(0, 8);
  const num = (c: OutCol) =>
    c.type === "money" || c.type === "number" || !!c.agg;
  return (
    <>
      <div
        className="table-wrap"
        role="region"
        aria-label={t("dash.table")}
        tabIndex={0}
      >
        <table className="data-table dash-table">
          <thead>
            <tr>
              {result.columns.map((c, i) => (
                <th key={i} scope="col" className={num(c) ? undefined : "left"}>
                  {outLabel(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, n) => (
              <tr key={n}>
                {row.map((v, i) => (
                  <td
                    key={i}
                    className={num(result.columns[i]!) ? "tabular" : "left"}
                  >
                    {cellText(result.columns[i]!, v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result.rows.length > rows.length ? (
        <Link className="dash-more small" to={`/my-reports/${report}`}>
          {tn("dash.more_rows", result.rows.length - rows.length)}
        </Link>
      ) : null}
    </>
  );
}

// ---- adding a tile -------------------------------------------------------------------------

function AddTileDialog({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (tile: TileSpec) => Promise<void>;
}) {
  const { data: reports } = useAction<SavedReport[]>("studio.reports");
  const [report, setReport] = useState("");
  const [view, setView] = useState<View>("table");
  const [size, setSize] = useState<Size>("md");
  const [busy, setBusy] = useState(false);
  const chosen = reports?.find((r) => r.id === report);
  const pick = (id: string) => {
    setReport(id);
    const r = reports?.find((x) => x.id === id);
    // A good first choice: bars over dates, a pie for one group, a pivot for two.
    if (!r) return;
    const s = r.spec;
    const best: View =
      s.group.length === 2
        ? "pivot"
        : s.group.length && s.group[0]!.includes("@")
          ? s.chart === "line"
            ? "line"
            : "bar"
          : s.group.length
            ? "pie"
            : "table";
    setView(best);
    setSize(
      best === "pivot" || best === "bar" || best === "line" ? "lg" : "md",
    );
  };
  return (
    <Dialog
      title={t("dash.add_tile")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="plus"
            busy={busy}
            disabled={!chosen || !!viewProblem(view, chosen.spec)}
            onClick={async () => {
              setBusy(true);
              try {
                await onAdd({
                  id: newId(),
                  report,
                  view,
                  size,
                  title: null,
                  compare: view === "number",
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("dash.add")}
          </Button>
        </>
      }
    >
      {!reports ? (
        <Skeleton h={120} />
      ) : !reports.length ? (
        <EmptyState
          icon="chart"
          title={t("dash.no_reports")}
          body={t("dash.no_reports_body")}
          action={<Link to="/my-reports/new">{t("rpt.new")}</Link>}
        />
      ) : (
        <>
          <Field label={t("dash.report")} hint={t("dash.report_hint")}>
            <select
              className="input"
              value={report}
              onChange={(e) => pick(e.target.value)}
              data-autofocus
            >
              <option value="">{t("dash.choose_report")}</option>
              {reports.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          {chosen ? (
            <>
              <fieldset className="dash-views">
                <legend>{t("dash.show_as")}</legend>
                {VIEWS.map((v) => {
                  const problem = viewProblem(v, chosen.spec);
                  return (
                    <button
                      key={v}
                      type="button"
                      className={`dash-view${view === v ? " active" : ""}`}
                      aria-pressed={view === v}
                      disabled={!!problem}
                      title={
                        problem
                          ? t(`dash.p_${problem}` as MessageKey)
                          : undefined
                      }
                      onClick={() => setView(v)}
                    >
                      <Icon name={VIEW_ICON[v]} size={22} />
                      <span>{t(`dash.v_${v}` as MessageKey)}</span>
                    </button>
                  );
                })}
              </fieldset>
              <div
                className="segmented dash-sizes"
                role="radiogroup"
                aria-label={t("dash.size")}
              >
                {SIZES.map((s) => (
                  <button
                    key={s}
                    role="radio"
                    aria-checked={size === s}
                    className={size === s ? "active" : ""}
                    onClick={() => setSize(s)}
                  >
                    {t(`dash.size_${s}` as MessageKey)}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </Dialog>
  );
}

// ---- pinning from a report -----------------------------------------------------------------

export function PinDialog({
  report,
  onClose,
}: {
  report: SavedReport;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: boards } = useAction<BoardRow[]>("studio.dashboards");
  const editable = boards?.filter((b) => b.can_edit) ?? [];
  const views = VIEWS.filter((v) => !viewProblem(v, report.spec));
  const [board, setBoard] = useState("");
  const [view, setView] = useState<View>(
    report.spec.group.length === 2
      ? "pivot"
      : report.spec.group.length
        ? "bar"
        : "table",
  );
  const { run, busy } = useSave();
  const target = board || editable[0]?.id || "";
  return (
    <Dialog
      title={t("dash.pin_title")}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="pushpin"
            busy={busy}
            disabled={!target}
            onClick={() =>
              void run(async () => {
                await action("studio.pin_report", {
                  dashboard: target,
                  report: report.id,
                  view,
                  size:
                    view === "number"
                      ? "sm"
                      : view === "pivot" || view === "bar" || view === "line"
                        ? "lg"
                        : "md",
                });
                navigate(`/dashboards/${target}`);
              }, t("dash.pinned"))
            }
          >
            {t("dash.pin")}
          </Button>
        </>
      }
    >
      {!boards ? (
        <Skeleton h={80} />
      ) : !editable.length ? (
        <EmptyState
          icon="layout"
          title={t("dash.empty")}
          action={<Link to="/dashboards">{t("dash.new")}</Link>}
        />
      ) : (
        <>
          <Field label={t("dash.which")}>
            <select
              className="input"
              value={target}
              onChange={(e) => setBoard(e.target.value)}
            >
              {editable.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("dash.show_as")}>
            <select
              className="input"
              value={view}
              onChange={(e) => setView(e.target.value as View)}
            >
              {views.map((v) => (
                <option key={v} value={v}>
                  {t(`dash.v_${v}` as MessageKey)}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}
    </Dialog>
  );
}
